import { useMemo } from 'react';

import { useParams } from 'react-router-dom';

import useFeatureFlags from 'api/useFeatureFlags';
import useIntegration from 'api/useIntegration';
import usePermissions from 'api/usePermissions';
import usePublisher from 'api/usePublisher';
import { useUserDetails } from 'api/useUserDetails';
import { EPublisherSolutionModel, Project } from 'common/contracts';
import { EPermissionGroup } from 'common/permissions.types';
import { EFeatureFlag, ELocalStorageKeys } from 'constants/enums';
import { useDeveloperSettingsEnabled } from 'hooks/useDeveloperSettingsEnabled';
import { usePortalStoreInfo } from 'hooks/usePortalStoreInfo';
import { useProfilesManager } from 'hooks/useProfilesManager';
import { ERouteContext } from 'routes/types';
import { localStorageUtil } from 'utils/localStorageUtil';
import { permissionsUtil } from 'utils/permissionsUtil';

import { ESettingsState } from './Settings.types';
import SharedSettingsView, { SharedSettingsTab } from './SharedSettingsView';

const getDefaultSettingsTab = (isDeveloperSettingsEnabled: boolean): ESettingsState => {
  if (isDeveloperSettingsEnabled) {
    return ESettingsState.DEVELOPER_SETTINGS;
  }
  return ESettingsState.COMPANY;
};

interface ISettingsViewProps {
  context: ERouteContext;
}

export const SettingsView: React.FC<ISettingsViewProps> = ({ context }) => {
  const { publisherId } = useParams();
  const { userDetails } = useUserDetails();
  const projectsDetails = userDetails?.projects;
  const { getPublisher } = usePublisher(publisherId);
  const { getIntegration } = useIntegration(publisherId);
  const { isProfilesFeatureFlagOn, isProfilesEnabled } = useProfilesManager();
  const { getPermissions, isSuperAdminByProject, isAdminByProject } = usePermissions();
  const permissions = getPermissions();
  const isDeveloperSettingsEnabled = useDeveloperSettingsEnabled();

  const projectItem = projectsDetails?.find(
    (item: Project) => item.publisherId === publisherId
  );

  const isCheckout =
    projectItem?.projectType === EPublisherSolutionModel.CHECKOUT;
  const { featureFlags } = useFeatureFlags({ publisherId });
  const hasFeatureFlagEventCenter =
    featureFlags?.[EFeatureFlag.DASHBOARD_EVENT_CENTER];
  const isAbbreviationsV2Enabled =
    featureFlags?.[EFeatureFlag.DASHBOARD_ABBREVIATION_V2];
  
  const { isPortalAssignedStore, isParkingDomain, isIntegrationsTabShown, isPageInactive } = usePortalStoreInfo();

  const isCheckoutUser =
    localStorageUtil.get(ELocalStorageKeys.PUBLISHER_SOLUTION_MODEL) ===
    EPublisherSolutionModel.CHECKOUT;

  const getTabs = useMemo(() => {
    const handleAuthTabShown = () => {
      if (context === ERouteContext.PORTAL_LEVEL_STORE) return;
      return (
        !isCheckout ||
        permissionsUtil.shouldHideGroupInPermission(
          permissions,
          EPermissionGroup.AUTHENTICATION,
          getPublisher.data?._id
        )
      );
    };

    const tabs: SharedSettingsTab[] = [];
    const isCompanyTabShown = !permissionsUtil.shouldHideGroupInPermission(
      permissions,
      EPermissionGroup.COMPANY,
      getPublisher.data?._id
    );
    const isUsersTabShown = !permissionsUtil.shouldHideGroupInPermission(
      permissions,
      EPermissionGroup.USERS,
      getPublisher.data?._id
    );

    const isIntegrationsProfilesTabShown =
      isIntegrationsTabShown && isProfilesFeatureFlagOn && isProfilesEnabled;
    const isSuperAdminForProject = isSuperAdminByProject(getPublisher.data?._id || '');
    const isStoreSettingsTabShown =
      (isSuperAdminForProject ||
        isAdminByProject(getPublisher.data?._id || '')) &&
      !isCheckoutUser;

    if (isDeveloperSettingsEnabled) {
      const isDeveloperSettingsTabShown = isCompanyTabShown || isIntegrationsTabShown;
      if (isDeveloperSettingsTabShown) {
        tabs.push({ label: 'Developer Settings', value: ESettingsState.DEVELOPER_SETTINGS });
      }
    } else {
      if (isCompanyTabShown) {
        tabs.push({ label: 'Company', value: ESettingsState.COMPANY });
      }
    }

    if (isUsersTabShown) {
      tabs.push({ label: 'Users', value: ESettingsState.USERS });
    }


      if (!isDeveloperSettingsEnabled && isIntegrationsTabShown) {
        tabs.push({ label: 'Integration', value: ESettingsState.INTEGRATION });
      }

    if (isIntegrationsProfilesTabShown) {
      tabs.push({
        label: 'Integration Environments',
        value: ESettingsState.PROFILES
      });
    }

    if (handleAuthTabShown()) {
      tabs.push({ label: 'Authentication', value: ESettingsState.PLAYER_AUTH });
    }

    if (isSuperAdminForProject) {
      tabs.push({ label: 'Payments', value: ESettingsState.PAYMENTS });
      if (!isCheckoutUser) {
        tabs.push({ label: 'Store Layout', value: ESettingsState.LAYOUT });
      }
    }

    if (
      (isSuperAdminForProject && (isParkingDomain || !isPortalAssignedStore)) ||
      (isAdminByProject(getPublisher.data?._id || '') &&
        hasFeatureFlagEventCenter)
    ) {
      tabs.push({ label: 'Event Center', value: ESettingsState.EVENTS });
    }

    if (isStoreSettingsTabShown) {
      const storeSettingsTabData = {
        label: isAbbreviationsV2Enabled ? 'Number Format' : 'Store Settings',
        value: ESettingsState.STORE_SETTINGS
      };
      const storeLayoutTabData = tabs.find(
        (tab) => tab.value === ESettingsState.LAYOUT
      );
      if (storeLayoutTabData) {
        tabs.splice(
          tabs.indexOf(storeLayoutTabData) + 1,
          0,
          storeSettingsTabData
        );
      } else {
        tabs.push(storeSettingsTabData);
      }
    }

    return tabs;
  }, [permissions, getPublisher.data?._id, isProfilesFeatureFlagOn, isProfilesEnabled, isCheckoutUser, hasFeatureFlagEventCenter, isAbbreviationsV2Enabled, isParkingDomain, isPortalAssignedStore, isIntegrationsTabShown, context, isCheckout, isDeveloperSettingsEnabled, isSuperAdminByProject, isAdminByProject]);

  const defaultTab = getDefaultSettingsTab(isDeveloperSettingsEnabled);

  return (
    <SharedSettingsView
      tabs={getTabs}
      defaultTab={defaultTab}
      headline="Settings"
      basePath={context === ERouteContext.PORTAL_LEVEL_STORE ? 'store' : 'project'}
      onTabChange={() => getIntegration.refetch()}
    />
  );
};
